import { PublicLayout } from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { Facebook, Linkedin } from "lucide-react";

const team = [
  {
    name: "Geom. Fabio Pesce",
    role: "Co-founder · Amministratore Condominiale",
    quote: "Trasparenza e serenità in ogni condominio.",
    bio: "Geometra e co-founder di Edilstudio. Guida le amministrazioni condominiali con contabilità chiara e gestione attenta di pratiche, manutenzioni e rapporti tra condòmini.",
    photo: "/img/team-fabio.jpg",
    facebook: "https://www.facebook.com/fabiopesce86",
    linkedin: "https://www.linkedin.com/in/fabio-pesce-509b16a3/",
  },
  {
    name: "Geom. Giulia Resuttana",
    role: "Co-founder · Agente Immobiliare",
    quote: "La casa giusta, con la consulenza giusta.",
    bio: "Geometra, co-founder e agente immobiliare. Segue compravendite, affitti e valutazioni con un approccio personalizzato, costruito sulle reali esigenze di ogni cliente.",
    photo: "/img/team-giulia.jpg",
    facebook: "https://www.facebook.com/giulia.resuttana/",
    linkedin: "https://www.linkedin.com/in/giulia-resuttana-71a493172/",
  },
  {
    name: "Lisa Ceccantini",
    role: "Front-office e Contabilità",
    quote: "Il primo sorriso che incontri in studio.",
    bio: "Si occupa di front-office e contabilità: accoglie i clienti, gestisce documenti e scadenze e tiene in ordine la macchina amministrativa dello studio.",
    photo: "/img/team-lisa.jpg",
  },
  {
    name: "Geom. Eleonora Fava",
    role: "Condomini e Cantieri",
    quote: "Dai documenti al cantiere, sempre sul pezzo.",
    bio: "Geometra dedicata a condomìni e cantieri: sopralluoghi, pratiche tecniche e coordinamento dei lavori per una gestione precisa e puntuale.",
    photo: "/img/team-eleonora.jpg",
    facebook: "https://www.facebook.com/eleonora.fava.7/",
    linkedin: "https://www.linkedin.com/in/eleonora-fava-2b4378254/",
  },
];

export default function ChiSiamo() {
  useSeo({
    title: "Chi Siamo | Edilstudio Immobiliare Scandicci e Firenze",
    description: "Edilstudio: ristrutturazioni, consulenza immobiliare e gestione condominiale a Scandicci. Un team di professionisti guidato da versatilità, passione e autenticità.",
    canonicalPath: "/chi-siamo",
  });

  return (
    <PublicLayout>
      {/* HERO — solo colore, senza immagine di sfondo */}
      <section className="bg-[#1B2A41] py-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#2472C8]/15" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#6FB3EC]">Chi siamo</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mt-3 leading-tight max-w-3xl">Verso la nuova concezione di Casa</h1>
          <p className="text-lg text-white/75 mt-5 max-w-2xl leading-relaxed">Un unico team, tre competenze: ristrutturazioni di qualità, consulenza immobiliare personalizzata e gestione condominiale professionale a Scandicci e Firenze.</p>
        </div>
      </section>

      {/* CHI SIAMO — testo unico, valori integrati */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="chi-intro">
        <p className="eyebrow text-[#2472C8]">La nostra identità</p>
        <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#1B2A41] mt-2">Versatili per natura, autentici per scelta</h2>
        <div className="mt-5 space-y-4 text-[#2C3A4F] leading-relaxed text-[17px]">
          <p>In Edilstudio uniamo tre anime — studio tecnico, agenzia immobiliare e amministrazione condominiale — in un unico riferimento affidabile. È questa versatilità a permetterci di seguirti davvero dall'inizio alla fine: ristrutturazioni curate nei dettagli, una consulenza immobiliare costruita sulle tue esigenze reali e una gestione condominiale attenta e trasparente.</p>
          <p>A muoverci sono passione e determinazione. La passione accende idee e soluzioni; la determinazione le trasforma in risultati concreti, dal primo incontro fino alla consegna. Ma prima di ogni progetto vengono le persone: crediamo in relazioni autentiche, nell'ascolto sincero e nella trasparenza, il modo in cui lavoriamo ogni giorno con clienti, partner e collaboratori.</p>
        </div>
      </section>

      {/* LA NOSTRA STORIA */}
      <section className="bg-[#EEF4F9] py-16" data-testid="chi-storia">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#2472C8]">La nostra storia</p>
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#1B2A41] mt-2">Dal 2012, un punto di riferimento a Scandicci</h2>
          <div className="mt-5 space-y-4 text-[#2C3A4F] leading-relaxed text-[17px]">
            <p>Edilstudio nasce nel 2012 dalla passione di <strong>Fabio</strong> e <strong>Giulia</strong>, due geometri con la voglia di fare le cose per bene. All'inizio eravamo soprattutto uno studio tecnico; anno dopo anno abbiamo ascoltato le esigenze del territorio e ampliato i nostri servizi all'amministrazione condominiale e all'agenzia immobiliare.</p>
            <p>Oggi siamo un gruppo affiatato di professionisti, sempre aggiornati su normative e mercato locale, con un obiettivo semplice: rendere ogni compravendita, affitto, ristrutturazione o gestione un percorso chiaro e senza pensieri.</p>
          </div>
        </div>
      </section>

      {/* IL NOSTRO TEAM — card orizzontali, foto B/N -> colore all'hover */}
      <section className="bg-[#14263D] py-20 relative overflow-hidden" data-testid="chi-team">
        <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#2472C8]/10" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#6FB3EC]">Il nostro team</p>
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2">Professionisti al tuo fianco</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
            {team.map((m, i) => (
              <div key={i} className="group bg-[#1B2A41] rounded-2xl border border-white/10 overflow-hidden flex flex-col sm:flex-row hover:border-[#2472C8]/40 hover:shadow-2xl hover:shadow-black/30 transition duration-300" data-testid={`team-${i}`}>
                <div className="sm:w-[42%] shrink-0 overflow-hidden aspect-[4/5] sm:aspect-auto">
                  <img
                    src={m.photo}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition duration-500"
                  />
                </div>
                <div className="flex-1 p-6 sm:p-7 flex flex-col justify-center">
                  <p className="font-serif italic text-[#F5A623] text-[15px] leading-snug">&ldquo;{m.quote}&rdquo;</p>
                  <h3 className="font-display text-xl font-bold text-white mt-3">{m.name}</h3>
                  <p className="text-[#6FB3EC] text-[11px] font-semibold uppercase tracking-[0.14em] mt-1.5">{m.role}</p>
                  <p className="text-sm text-white/65 leading-relaxed mt-3">{m.bio}</p>
                  {(m.facebook || m.linkedin) && (
                    <div className="flex gap-2 mt-4">
                      {m.facebook && (
                        <a href={m.facebook} target="_blank" rel="noopener noreferrer" aria-label={`Facebook di ${m.name}`} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/70 hover:bg-[#2472C8] hover:text-white transition-colors" data-testid={`team-${i}-facebook`}>
                          <Facebook className="w-4 h-4" />
                        </a>
                      )}
                      {m.linkedin && (
                        <a href={m.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`LinkedIn di ${m.name}`} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-white/70 hover:bg-[#2472C8] hover:text-white transition-colors" data-testid={`team-${i}-linkedin`}>
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B2A41] py-14 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-white">Lavoriamo insieme al tuo prossimo progetto</h2>
          <a href="/contact/" className="inline-block mt-6 bg-[#F5A623] hover:bg-[#E0961C] text-[#1B2A41] font-bold px-7 py-3.5 rounded-full transition-colors">Contattaci</a>
        </div>
      </section>
    </PublicLayout>
  );
}
